import { FormEvent, ReactNode } from 'react';
import { createPortal } from 'react-dom';

interface Props {
  title: ReactNode;
  icon?: string;
  onClose: () => void;
  footer?: ReactNode;
  children: ReactNode;
  bodyStyle?: React.CSSProperties;
  // Forwarded to the scrollable .modal-body element (e.g. for scroll detection).
  bodyRef?: React.Ref<HTMLDivElement>;
  dialogStyle?: React.CSSProperties;
  large?: boolean;
  // When set, the modal wraps its header/body/footer in a <form> so submit/Enter works.
  onSubmit?: (e: FormEvent) => void;
  formClassName?: string;
  subHeader?: ReactNode;
}

// Bootstrap-style modal shell with a backdrop. Click on the backdrop closes;
// click inside the dialog does not. Caller controls open/close via parent state.
// Renders via portal into document.body to avoid z-index and aria-hidden issues
// with Angular's container div.
export function Modal({ title, icon, onClose, footer, children, bodyStyle, bodyRef, dialogStyle, large, onSubmit, formClassName, subHeader }: Props) {
  const inner = (
    <div className="modal-content">
      <div className="modal-header">
        <button type="button" className="close" onClick={onClose} aria-label="Close">&times;</button>
        <h4 className="modal-title">
          {icon && <><i className={icon} aria-hidden="true" />{' '}</>}
          {title}
        </h4>
      </div>
      {subHeader && <div className="modal-sub-header">{subHeader}</div>}
      <div className="modal-body" style={bodyStyle} ref={bodyRef}>
        {children}
      </div>
      {footer && <div className="modal-footer">{footer}</div>}
    </div>
  );
  const dialogClassName = `modal-dialog${large ? ' modal-lg iio-large-modal' : ''}`;
  const modal = (
    <div
      className="modal"
      style={{ display: 'block', background: 'rgba(0,0,0,0.5)' }}
      onClick={onClose}
    >
      <div className={dialogClassName} style={dialogStyle} onClick={e => e.stopPropagation()}>
        {onSubmit ? (
          <form
            onSubmit={e => { e.preventDefault(); onSubmit(e); }}
            className={formClassName}
            noValidate
          >
            {inner}
          </form>
        ) : inner}
      </div>
    </div>
  );
  return createPortal(modal, document.body);
}
